/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * When the classic UI offers "Try the new look". Kept free of auth/axios
 * imports so it runs under CRA's jest.
 *
 * Offered to anyone on the classic look who has not picked a look yet. Once
 * they switch, the choice is stored on the server (/users/me/ui-preferences)
 * and every later sign-in opens in v2 until they choose "Switch back to
 * classic" from the profile menu. Someone who made that choice is not asked
 * again: the profile menu still offers the new look.
 */

import { UI_LEGACY } from "hooks/uiVersion";

export const DISMISS_STORAGE_KEY = "pearl.tryNewLook.dismissedFor";

/** The `jti` of an access token, or null. One sign-in, one jti. */
export function sessionIdFromToken(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return payload && payload.jti ? String(payload.jti) : null;
  } catch (e) {
    return null;
  }
}

export function shouldOfferNewLook({ enabled, ready, isV2, userChoice, locked, sessionId, dismissedFor }) {
  if (!enabled || !ready || isV2 || locked) return false;
  // They chose classic themselves: respect it.
  if (userChoice === UI_LEGACY) return false;
  // Dismissed for this sign-in. A new sign-in brings it back.
  if (sessionId && dismissedFor === sessionId) return false;
  return true;
}

function safeSession() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
}

export function readDismissedFor(storage = safeSession()) {
  try {
    return storage ? storage.getItem(DISMISS_STORAGE_KEY) : null;
  } catch (e) {
    return null;
  }
}

export function writeDismissedFor(sessionId, storage = safeSession()) {
  try {
    if (storage && sessionId) storage.setItem(DISMISS_STORAGE_KEY, sessionId);
  } catch (e) {
    // Blocked storage: the banner simply stays dismissed until reload.
  }
}
